/**
 * The Markdown of a persona, read into a tree that components/plane-markdown.js renders: the part
 * of CommonMark a persona uses (headings, paragraphs, lists, quotes, code, rules; strong, emphasis,
 * code, links), not all of it. No HTML passes through: the text stays text, and a link goes only
 * to http, https or mailto addresses.
 *
 * A single newline is a line break, as a person writing "one rule per line" expects, not a space.
 *
 * Blocks: { type: 'heading', level, inline } · { type: 'paragraph', inline } · { type: 'code', text }
 * · { type: 'quote', blocks } · { type: 'rule' } · { type: 'list', ordered, start, items: [blocks] }.
 * Inline: { type: 'text', text } · { type: 'strong' | 'em' | 'del', children } · { type: 'code', text }
 * · { type: 'link', href, children } · { type: 'break' }.
 */

const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const HEADING = /^ {0,3}(#{1,6})(?:\s+(.*?))?(?:\s+#+)?\s*$/;
const RULE = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/;
const QUOTE = /^ {0,3}>\s?/;
const ITEM = /^(\s*)([-*+]|(\d{1,9})[.)])(\s+|$)(.*)$/;

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;
const URL = /^https?:\/\/[^\s<]+/;
const ESCAPABLE = /[\\`*_{}[\]()#+\-.!~>|]/;

/** The blocks of a Markdown source; empty for nothing, or only blanks. */
export function parseMarkdown(source) {
  return parseBlocks((source ?? '').replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split('\n'));
}

function isBlank(line) {
  return line.trim() === '';
}

/** Whether a line starts a block of its own, ending the paragraph above it. */
function startsBlock(line) {
  return FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) || ITEM.test(line);
}

function indentOf(line) {
  return line.length - line.trimStart().length;
}

function parseBlocks(lines) {
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (isBlank(line)) {
      i++;
      continue;
    }
    const fence = line.match(FENCE);
    if (fence) {
      const close = new RegExp(`^ {0,3}${fence[1][0]}{${fence[1].length},}\\s*$`);
      const text = [];
      i++;
      while (i < lines.length && !close.test(lines[i])) {
        text.push(lines[i]);
        i++;
      }
      blocks.push({ type: 'code', text: text.join('\n') });
      i++;
      continue;
    }
    const heading = line.match(HEADING);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, inline: parseInline(heading[2] ?? '') });
      i++;
      continue;
    }
    // before the list: "- - -" and "* * *" are rules, not items
    if (RULE.test(line)) {
      blocks.push({ type: 'rule' });
      i++;
      continue;
    }
    if (QUOTE.test(line)) {
      const quoted = [];
      while (i < lines.length && !isBlank(lines[i]) && (QUOTE.test(lines[i]) || !startsBlock(lines[i]))) {
        quoted.push(lines[i].replace(QUOTE, ''));
        i++;
      }
      blocks.push({ type: 'quote', blocks: parseBlocks(quoted) });
      continue;
    }
    if (ITEM.test(line)) {
      i = parseList(lines, i, blocks);
      continue;
    }
    const text = [];
    while (i < lines.length && !isBlank(lines[i]) && (text.length === 0 || !startsBlock(lines[i]))) {
      text.push(lines[i].trim());
      i++;
    }
    blocks.push({ type: 'paragraph', inline: parseInline(text.join('\n')) });
  }
  return blocks;
}

/**
 * A list from line `start`: its items, each the blocks of its own lines (a nested list among
 * them), pushed on `blocks`. Returns the line after it.
 */
function parseList(lines, start, blocks) {
  const first = lines[start].match(ITEM);
  const base = first[1].length;
  const ordered = first[3] !== undefined;
  const items = [];
  let i = start;
  while (i < lines.length) {
    const item = lines[i].match(ITEM);
    if (!item || item[1].length > base + 1 || (item[3] !== undefined) !== ordered) {
      break;
    }
    // the lines of the item: indented under its text, or a paragraph running on
    const content = item[1].length + item[2].length + Math.max(1, Math.min(item[4].length, 4));
    const own = [item[5]];
    i++;
    while (i < lines.length) {
      const line = lines[i];
      if (isBlank(line)) {
        const next = lines.slice(i + 1).find((candidate) => !isBlank(candidate));
        if (next === undefined || indentOf(next) <= base) {
          break;
        }
        own.push('');
      } else if (indentOf(line) > base) {
        own.push(line.slice(Math.min(content, indentOf(line))));
      } else if (!startsBlock(line) && !isBlank(own[own.length - 1])) {
        own.push(line.trim());
      } else {
        break;
      }
      i++;
    }
    items.push(parseBlocks(own));
    // a blank line between two items keeps the list going
    if (i < lines.length && isBlank(lines[i])) {
      const next = lines.slice(i).findIndex((candidate) => !isBlank(candidate));
      if (next === -1 || !ITEM.test(lines[i + next]) || indentOf(lines[i + next]) > base + 1) {
        break;
      }
      i += next;
    }
  }
  blocks.push({ type: 'list', ordered, start: ordered ? Number(first[3]) : 1, items });
  return i;
}

/** The inline nodes of a paragraph, a heading or an item. */
export function parseInline(text) {
  const nodes = [];
  let plain = '';
  const push = (node) => {
    if (plain) {
      nodes.push({ type: 'text', text: plain });
      plain = '';
    }
    nodes.push(node);
  };
  let i = 0;
  while (i < text.length) {
    const char = text[i];
    const rest = text.slice(i);
    if (char === '\\' && i + 1 < text.length && ESCAPABLE.test(text[i + 1])) {
      plain += text[i + 1];
      i += 2;
      continue;
    }
    if (char === '\n') {
      plain = plain.replace(/\\$/, '');
      push({ type: 'break' });
      i++;
      continue;
    }
    if (char === '`') {
      const ticks = rest.match(/^`+/)[0];
      const end = text.indexOf(ticks, i + ticks.length);
      if (end !== -1) {
        push({ type: 'code', text: text.slice(i + ticks.length, end).replace(/^ (.+) $/, '$1') });
        i = end + ticks.length;
        continue;
      }
      plain += ticks;
      i += ticks.length;
      continue;
    }
    const span = delimited(text, i);
    if (span) {
      push({ type: span.type, children: parseInline(span.inner) });
      i = span.end;
      continue;
    }
    if (char === '[') {
      const link = linkAt(text, i);
      if (link) {
        const children = parseInline(link.label);
        if (SAFE_HREF.test(link.href)) {
          push({ type: 'link', href: link.href, children });
        } else {
          children.forEach(push);
        }
        i = link.end;
        continue;
      }
    }
    if (char === '<') {
      const auto = rest.match(/^<((?:https?:\/\/|mailto:)[^\s<>]+)>/i);
      if (auto) {
        push({ type: 'link', href: auto[1], children: [{ type: 'text', text: auto[1].replace(/^mailto:/i, '') }] });
        i += auto[0].length;
        continue;
      }
    }
    if ((char === 'h' || char === 'H') && !/\w/.test(text[i - 1] ?? '')) {
      const url = rest.match(URL);
      if (url) {
        // the full stop or the bracket after an address is the sentence's, not the address's
        const href = trimUrl(url[0]);
        push({ type: 'link', href, children: [{ type: 'text', text: href }] });
        i += href.length;
        continue;
      }
    }
    plain += char;
    i++;
  }
  if (plain) {
    nodes.push({ type: 'text', text: plain });
  }
  return nodes;
}

/** An address found in running text, without the punctuation of the sentence after it: "(see https://a.b)." */
function trimUrl(url) {
  let href = url.replace(/[.,;:!?'"]+$/, '');
  while (href.endsWith(')') && href.split('(').length < href.split(')').length) {
    href = href.slice(0, -1).replace(/[.,;:!?'"]+$/, '');
  }
  return href;
}

/** Strong, emphasis or strikethrough starting at `i`: its type, its text and where it ends; null if none. */
function delimited(text, i) {
  for (const [delimiter, type] of [
    ['**', 'strong'],
    ['__', 'strong'],
    ['~~', 'del'],
    ['*', 'em'],
    ['_', 'em'],
  ]) {
    if (!text.startsWith(delimiter, i)) {
      continue;
    }
    const open = i + delimiter.length;
    // "a * b" and snake_case are not emphasis
    if (/\s/.test(text[open] ?? ' ') || (delimiter[0] === '_' && /\w/.test(text[i - 1] ?? ''))) {
      continue;
    }
    let end = text.indexOf(delimiter, open + 1);
    while (end !== -1) {
      const single = delimiter.length === 1;
      const doubled = single && (text[end + 1] === delimiter || text[end - 1] === delimiter);
      const intraword = delimiter[0] === '_' && /\w/.test(text[end + delimiter.length] ?? '');
      if (!/\s/.test(text[end - 1]) && !doubled && !intraword) {
        return { type, inner: text.slice(open, end), end: end + delimiter.length };
      }
      end = text.indexOf(delimiter, end + (doubled ? 2 : 1));
    }
  }
  return null;
}

/** A link "[label](href)" starting at `i`: its label, its address and where it ends; null if none. */
function linkAt(text, i) {
  let depth = 0;
  let close = -1;
  for (let j = i; j < text.length; j++) {
    if (text[j] === '\\') {
      j++;
    } else if (text[j] === '[') {
      depth++;
    } else if (text[j] === ']' && --depth === 0) {
      close = j;
      break;
    }
  }
  if (close === -1) {
    return null;
  }
  const target = text.slice(close + 1).match(/^\(\s*<?([^\s()<>]*)>?(?:\s+"[^"]*")?\s*\)/);
  return target ? { label: text.slice(i + 1, close), href: target[1], end: close + 1 + target[0].length } : null;
}
